import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { ExaminationSession } from '@lyceo/lyceo-pupil-app/practise/util-models';
import {
  ExaminationSessionActions,
  ExaminationSessionAnswersModalActions,
} from './examination-session.actions';
import { PractiseBookPageActions } from '../practise/practise.actions';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import { compareDesc, parseISO } from 'date-fns';
import { mapExaminationSession } from './examination-session.functions';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const EXAMINATION_SESSION_FEATURE_KEY: string = 'examinationSession';

export interface ExaminationSessionState
  extends EntityState<ExaminationSession> {
  loadedQuestionnaireIds: string[];
  selectedId: string | undefined;
  detailedCallState: CallState;
}

export function sortByFinishedAt(
  a: ExaminationSession,
  b: ExaminationSession,
): number {
  return compareDesc(parseISO(a.finishedAt), parseISO(b.finishedAt));
}

export const examinationSessionAdapter: EntityAdapter<ExaminationSession> =
  createEntityAdapter<ExaminationSession>({
    sortComparer: sortByFinishedAt,
  });

export const initialExaminationSessionState: ExaminationSessionState =
  examinationSessionAdapter.getInitialState({
    loadedQuestionnaireIds: [],
    selectedId: undefined,
    detailedCallState: LoadingState.INIT,
  });

const reducer: ActionReducer<ExaminationSessionState, Action> = createReducer(
  initialExaminationSessionState,
  on(
    AuthActions.logoutSuccess,
    (): ExaminationSessionState => initialExaminationSessionState,
  ),
  on(
    ExaminationSessionActions.loadExaminationSessionsSuccess,
    (state, action): ExaminationSessionState =>
      examinationSessionAdapter.upsertMany(
        action.examinationSessions.map((examinationSessionDto) =>
          mapExaminationSession(examinationSessionDto),
        ),
        {
          ...state,
          loadedQuestionnaireIds: [
            ...state.loadedQuestionnaireIds,
            ...action.questionnaireIds,
          ],
        },
      ),
  ),
  on(
    ExaminationSessionAnswersModalActions.modalClosed,
    (state): ExaminationSessionState => ({
      ...state,
      selectedId: undefined,
    }),
  ),
  on(
    PractiseBookPageActions.viewExerciseResultClicked,
    (state, action): ExaminationSessionState => ({
      ...state,
      detailedCallState: LoadingState.LOADING,
      selectedId: action.exerciseResult.meta?.id,
    }),
  ),
  on(
    ExaminationSessionActions.getExaminationSessionDetailedSuccess,
    (state): ExaminationSessionState => ({
      ...state,
      detailedCallState: LoadingState.LOADED,
    }),
  ),
  on(
    ExaminationSessionActions.getExaminationSessionDetailedFailure,
    (state, { error }): ExaminationSessionState => ({
      ...state,
      detailedCallState: { errorMsg: error.message },
    }),
  ),
);

export function examinationSessionReducer(
  state: ExaminationSessionState | undefined,
  action: Action,
): ExaminationSessionState {
  return reducer(state, action);
}
